/**
 * AMENITIES ARE PICKED, NOT TYPED — port of the monolith's amenity layer
 * (index.html L10952–11095), audit row 17.
 *
 * WHY (the monolith's own note, kept because the reasoning is the spec)
 * ---------------------------------------------------------------------
 * The amenity blocks declare `iconN` + `labelN` pairs: 3, 4, or 8 of them. That
 * rendered as up to 17 fields — a Bynder picker and a text box per amenity — so
 * setting four amenities meant eight separate edits and nothing stopped a division
 * pointing an amenity at a photo of a house. Instead: one list, click to toggle.
 *
 * THE LIBRARY IS NOT INVENTED HERE. It is the set of icon/label DEFAULTS the
 * amenity blocks themselves ship with, so the icons written into the email are the
 * hosted SFMC ones Layne already uses. The monolith reads them off
 * `window.__ECH_LIVE[name].gr.fields`; here the same declared maps arrive through
 * `echRegistry`, which is the seam — `amenLib()` stays pure so this file never
 * imports the registry (that would be a cycle: the registry's renderer calls
 * `amenTrimHtml` below).
 *
 * MIN 2, MAX 8 (2026-08-18, per Jack). The floor is a design rule — one amenity in
 * a row built for a grid reads as a mistake. The ceiling is 8 because that is the
 * most any amenity block declares (Layne's merged ECH_Amenities); a block with
 * fewer cells can only go as high as it has, and the panel says which.
 */
import type { FieldDescriptor } from "../types/blocks";
import type { GuardrailField } from "./guardrails";

export const AMEN_MIN = 2;
export const AMEN_MAX = 8;

/** the blocks the icon library is read out of, in preference order (monolith L10974) */
/* ECH_AmenitiesSection ("Bar + 1-8 Icons") joined 2026-08-21 — it was never in
   this list, so its panel fell through to 8 raw photo cards + label fields
   instead of the picker (Jack: "what happened with the prior set up?"). Its
   icon1..8/label1..8 declaration is the same shape the picker already reads. */
export const AMEN_LIB_BLOCKS = ["ECH_Amenities", "ECH_4Amenities", "ECH_3Amenities", "ECH_AmenitiesSection"];

/** one declared `iconN`/`labelN` slot on a block */
export interface AmenPair { n: number; icon: string; label: string }
/** one entry in the icon library. `group` is the SFMC subfolder the icon sits in
 *  ("Water", "Play", …) — absent when the library came from the blocks' declared
 *  defaults, which carry no taxonomy. */
export interface AmenIcon { label: string; url: string; group?: string }

/* ── FALLBACK LIBRARY (2026-08-21). Layne consolidated the amenity blocks into ONE
   live block (ECH_Amenities, "Bar + 1-8 Icons") whose icon defaults ship EMPTY —
   measured live: every icon₁..₈ default is "". The library used to be built from
   the retired 3-/4-across blocks' declared defaults, so it starved and the picker
   silently fell back to 8 raw photo cards (Jack: "what happened with the prior
   set up?"). These are the HOSTED icons those blocks declared (image.news.lennar.com
   — email-safe; the bundled amenity-icons.json data-URLs are NOT, Gmail strips
   them). Real fix worth asking Layne for: restore icon defaults on the live block
   in Content Builder, which supersedes this list automatically. */
export const AMEN_FALLBACK: AmenIcon[] = [
  { label: "Park", url: "https://image.news.lennar.com/lib/fe9813727663077d74/m/1/dc6b2b61-28c8-4c85-8497-f4eae9c57594.png" },
  { label: "Playground", url: "https://image.news.lennar.com/lib/fe9813727663077d74/m/1/b5eaaa5d-0735-4b01-8eb7-6cd06d7a96f6.png" },
  { label: "Tot Lot", url: "https://image.news.lennar.com/lib/fe8a1372766d017576/m/1/Tot+lot_darkmode.png" },
  { label: "Pond", url: "https://image.news.lennar.com/lib/fe9813727663077d74/m/1/ae3f87fe-cb8f-426c-8559-9a9a956dd212.png" },
];
/* ── THE FOLDER LIBRARY (2026-08-21) ────────────────────────────────────────
   Layne: "all the options are in this folder: Content Builder > Test - Jack
   Blocks > Amenity Icons ... they are all named 'Pool_darkmode' or
   'Playground_darkmode' so we could take whatever is before the underscore and
   use that as the name, but still allow divisions to edit."

   So the inventory is the FOLDER, not the blocks' declared defaults — which is
   what starved the library in the first place. The worker reads the folder
   (/amenity-icons) and hands back {label, name, url}; this holds the result so
   `echAmenLib()` can stay synchronous for render.

   Measured live on first read: 45 icons, all 45 with a hosted
   image.news.lennar.com URL, against the 4 the fallback could offer.

   DE-DUPED ON LABEL, first wins. The folder really does carry two pairs that
   collapse — "Community Garden_darkmode" / "Community Garden_darkmode1" and
   "Lake_darkmode" / "Lake_darkmode1" — and two identical-looking options in a
   picker is a worse bug than 44 icons instead of 45. Raised with Layne; if the
   duplicate is deliberate art, the fix is a distinct name in the folder, which
   this picks up automatically.

   ⚠️ THIS WAS ASPIRATIONAL UNTIL 2026-08-28. The two pairs did NOT collapse: the
   worker's label strip was anchored `_darkmode$`, which the numbered takes miss, so
   the feed shipped the literal labels "Lake darkmode1" and "Community Garden
   darkmode1" — measured live, 45 assets and 45 distinct labels, nothing de-duped.
   They were unreachable from Airtable (no amenity value reads like that) but the
   picker still offered them, so a division could have put one in a live email. Fixed
   in the BE's /amenity-icons (`\s*darkmode\s*\d*\s*$` after collapsing separators);
   the de-dupe below is what now takes 45 assets down to the 43 the picker offers. */
let FOLDER_LIB: AmenIcon[] = [];
let FOLDER_ORDER: string[] = [];
let FOLDER_V = 0;

/** the folder library, or [] before it lands */
export function amenFolderLib(): AmenIcon[] { return FOLDER_LIB; }
/**
 * Group order, exactly as the worker sent it — which is the order the subfolders
 * appear in Content Builder, with "Other" forced last.
 *
 * The ORDER IS A SERVER DECISION on purpose. Sorting groups here would be a second
 * opinion about the taxonomy living in a second place, which is the thing moving
 * the categories into SFMC was meant to stop.
 */
export function amenGroupOrder(): string[] { return FOLDER_ORDER; }
/** bumps whenever the folder library changes — a React dependency, nothing more */
export function amenFolderVersion(): number { return FOLDER_V; }

/**
 * Store what the worker returned. Rows with no url are dropped HERE rather than
 * server-side, so /amenity-icons can keep reporting them for whoever has to fix
 * the folder.
 */
export function setAmenFolderLib(
  rows: { label?: string; url?: string; group?: string }[] | null | undefined,
  order?: string[] | null,
): void {
  const seen: Record<string, 1> = {};
  const lib: AmenIcon[] = [];
  for (const r of rows || []) {
    const label = String((r && r.label) || "").trim();
    const url = String((r && r.url) || "").trim();
    if (!label || !url) continue;
    const k = label.toLowerCase();
    if (seen[k]) continue;
    seen[k] = 1;
    const group = String((r && r.group) || "").trim();
    lib.push(group ? { label, url, group } : { label, url });
  }
  lib.sort((a, b) => a.label.localeCompare(b.label));
  FOLDER_LIB = lib;
  /* trust the server's order, but only for groups that actually have an icon —
     an empty header is a rendering artefact, not information */
  const present: Record<string, 1> = {};
  lib.forEach((i) => { if (i.group) present[i.group] = 1; });
  const fromServer = (order || []).filter((g) => present[g]);
  /* any group the server did not list (it can only be a stale cache) goes after,
     in first-seen order, so an icon is never silently dropped from the grid */
  const extra: string[] = [];
  lib.forEach((i) => { if (i.group && fromServer.indexOf(i.group) === -1 && extra.indexOf(i.group) === -1) extra.push(i.group); });
  FOLDER_ORDER = fromServer.concat(extra);
  FOLDER_V++;
}

/** test seam only */
export function __resetAmenFolder(): void { FOLDER_LIB = []; FOLDER_ORDER = []; FOLDER_V = 0; }

/** what is currently in a slot */
export interface AmenPick { label: string; url: string }

/**
 * Detect the `icon<N>` / `label<N>` pairs a block DECLARES (monolith
 * `__amenPairs`, L10964–10969).
 *
 * Off the declared fields, never a hardcoded cell count: the live template has
 * already replaced ECH_BlueHeader + ECH_4Amenities with one combined
 * "Amenities Section (Bar + 1-8 Icons)" block, and the panel picked that up with
 * no code change precisely because the pairs come from the block.
 */
export function amenPairs(fields?: readonly FieldDescriptor[] | null): AmenPair[] {
  const out: AmenPair[] = [];
  for (const f of fields || []) {
    const mm = /^icon(\d+)$/.exec(String(f.k || ""));
    if (mm) out.push({ n: +mm[1], icon: String(f.k), label: "label" + mm[1] });
  }
  out.sort((a, b) => a.n - b.n);
  return out.slice(0, AMEN_MAX);
}

/**
 * The icon library, built from the amenity blocks' own declared defaults
 * (monolith `__amenLib`, L10970–10989). Takes the declared field maps rather than
 * reading a global — the caller (echRegistry.echAmenLib) supplies them in
 * AMEN_LIB_BLOCKS order.
 *
 * A slot only enters the library when BOTH its icon URL and its label default are
 * non-empty, which is also what keeps a block's blank cells out of the list.
 * Deduped on the label, sorted by label so the panel reads alphabetically.
 */
export function amenLib(maps: (Record<string, GuardrailField> | null | undefined)[]): AmenIcon[] {
  const lib: AmenIcon[] = [];
  const seen: Record<string, 1> = {};
  for (const f of maps || []) {
    if (!f) continue;
    for (const k of Object.keys(f)) {
      const mm = /^icon(\d+)$/.exec(k);
      if (!mm) continue;
      const url = String((f[k] || {}).default ?? "").trim();
      const lab = String((f["label" + mm[1]] || {}).default ?? "").trim();
      if (!url || !lab || seen[lab.toLowerCase()]) continue;
      seen[lab.toLowerCase()] = 1;
      lib.push({ label: lab, url });
    }
  }
  lib.sort((a, b) => a.label.localeCompare(b.label));
  return lib;
}

/* ── AMENITIES FROM AIRTABLE (monolith L10995–11023) ─────────────────────────
   The Communities row carries an Amenities multi-select (5,136 rows populated),
   but its labels don't match the icon labels: "Park(s)" vs Park, "Swimming Pool"
   vs Pool, "Community Gardens" vs Community Garden. And plenty of its values have
   no icon at all (Bocce Ball, Fishing Lake, Trails, Splash Park) — those are
   skipped rather than guessed at. */
/**
 * Airtable's amenity vocabulary → the SFMC icon library's vocabulary.
 *
 * REBUILT 2026-08-21 against the REAL data. The old map had ten entries and was
 * written when the library held four icons; measured against /refdata's 2,724
 * communities and their 48 distinct values, only 85% of selections resolved to an
 * icon — and the failures were almost never a missing icon. "Exercise/Fitness
 * Center" (462 selections) matched nothing while `Fitness Center` sat in the
 * folder; so did Sport Court(s) (221), Gated (125), Greenbelt(s) (125), BBQs
 * (114), Fully Maintained Lawns (67), Manned Security Gate (46), Aerobics
 * Center/Studio (42) and Waterfront Homesite(s) (33).
 *
 * TWO ENTRIES WERE ACTIVELY WRONG and are gone: `lake` and `fishing lake` both
 * pointed at **Pond**, which was correct when Pond was the only water icon and
 * became a lie the moment `Lake` and `Fishing Lake` were added. A lakefront
 * community was shipping a pond. See the precedence note in `amenFromAirtable` —
 * a synonym must never beat an exact icon name again.
 *
 * DELIBERATELY ABSENT (2026-08-21, Jack: "if no amenities leave as blank"). These
 * five values have no confident icon, so they map to NOTHING rather than to a
 * near neighbour — 34 selections in total:
 *   Cabana (14) · Restaurant & Bar (13) · Outdoor Gym (4) · Walking Path (4) ·
 *   Event Lawn (4) · and "Sports Fields" (10), which is generic where every
 *   field icon is a specific sport — a baseball community must not ship a soccer
 *   ball. These are the icon requests to send Layne, not gaps to paper over.
 */
export const AMEN_SYNONYM: Record<string, string> = {
  /* water */
  "swimming pool": "Pool", pool: "Pool", "community pool": "Pool", "resort-style pool": "Pool",
  "pool house": "Pool House",
  "splash park": "Splash Park", "splash pad": "Splash Park",
  pond: "Pond", ponds: "Pond",
  /* NO `lake` / `fishing lake` HERE — both are real icons now, matched exactly. */
  "boat launch": "Boat Launch", "boat ramp": "Boat Launch",
  marina: "Marina",
  "waterfront homesite(s)": "Waterfront Homesite", "waterfront homesites": "Waterfront Homesite",
  /* fitness */
  "exercise/fitness center": "Fitness Center", "fitness center": "Fitness Center",
  "exercise center": "Fitness Center", "wellness center": "Fitness Center",
  "aerobics center/studio": "Aerobics Studio", "aerobics center": "Aerobics Studio",
  trails: "Trails", trail: "Trails",
  /* courts & fields — only where the sport is unambiguous */
  "sport court(s)": "Sport Court", "sport courts": "Sport Court",
  "bocce ball": "Bocce Ball", bocce: "Bocce Ball",
  "shuffle board": "Shuffle Board", shuffleboard: "Shuffle Board",
  "soccer fields": "Soccer Fields", "soccer field": "Soccer Fields",
  "baseball field": "Baseball Field", "baseball fields": "Baseball Field",
  /* gathering */
  clubhouse: "Clubhouse", "club house": "Clubhouse",
  "community center": "Community Center",
  bbqs: "Barbeque", bbq: "Barbeque", barbecue: "Barbeque", barbeque: "Barbeque",
  "bbq grill": "BBQ Grill", "bbq grills": "BBQ Grill",
  "fire pit": "Firepit", "fire pits": "Firepit", firepit: "Firepit",
  "picnic area": "Picnic Area", "picnic areas": "Picnic Area",
  pavilion: "Pavilion", pavilions: "Pavilion",
  /* play */
  playground: "Playground", playgrounds: "Playground",
  "tot lot": "Tot Lot", "tot lots": "Tot Lot",
  "dog park": "Dog Park", "bark park": "Dog Park",
  /* grounds */
  "park(s)": "Park", parks: "Park", park: "Park",
  "greenbelt(s)": "Greenbelt", greenbelts: "Greenbelt",
  "community gardens": "Community Garden", "community garden": "Community Garden",
  "fully maintained lawns": "Maintained Lawns", "maintained lawns": "Maintained Lawns",
  /* access */
  gated: "Gated Security", "gated community": "Gated Security", "gated entry": "Gated Security",
  "manned security gate": "Manned Gate", "manned gate": "Manned Gate", "guard gate": "Manned Gate",
  "rv parking": "RV", rv: "RV",
};

/**
 * Airtable's own plural / "(s)" habits, resolved mechanically so a new option
 * spelled "Pavilion(s)" works without a code change. Tried only AFTER an exact
 * icon name and the explicit map, so it can never override either.
 */
function amenLoose(k: string): string[] {
  const out: string[] = [];
  const noParen = k.replace(/\(s\)/g, "").replace(/\s{2,}/g, " ").trim();
  if (noParen !== k) out.push(noParen);
  const base = noParen || k;
  if (/s$/.test(base) && !/ss$/.test(base)) out.push(base.replace(/s$/, ""));
  if (/es$/.test(base)) out.push(base.replace(/es$/, ""));
  return out;
}

/**
 * The community's own amenities, mapped onto the icon library (monolith
 * `__amenFromAirtable`, L11008–11023). `amenities` is the pipe-joined Airtable
 * multi-select off /refdata's `addr` row.
 *
 * Order follows the LIBRARY, not Airtable, so the email is consistent community to
 * community; only amenities the community actually claims are returned.
 */
export function amenFromAirtable(amenities: unknown, lib: AmenIcon[]): string[] {
  const s = String(amenities || "").trim();
  if (!s || !lib.length) return [];
  const have: Record<string, string> = {};
  lib.forEach((a) => { have[a.label.toLowerCase()] = a.label; });
  const claimed: Record<string, 1> = {};
  s.split("|").forEach((raw) => {
    const k = String(raw || "").trim().toLowerCase();
    /* "None" and "TBD" are real Airtable options meaning "we have not said" —
       990 selections between them. Neither is an amenity. */
    if (!k || k === "none" || k === "tbd") return;
    /* AN EXACT ICON NAME WINS, ALWAYS — the old order asked the synonym map first,
       which is how "Lake" kept resolving to Pond after a Lake icon existed. A
       synonym is a translation for a word the library does NOT have; the moment
       the library has the word, the translation is stale by definition. */
    let mapped = have[k] || AMEN_SYNONYM[k] || null;
    if (!mapped) {
      for (const alt of amenLoose(k)) {
        mapped = have[alt] || AMEN_SYNONYM[alt] || null;
        if (mapped) break;
      }
    }
    if (mapped && have[mapped.toLowerCase()]) claimed[mapped] = 1;
  });
  return lib.map((a) => a.label).filter((l) => claimed[l]);
}

/** what is currently on the block, in slot order (monolith `__amenPicked`) */
export function amenPicked(data: Record<string, unknown>, pairs: AmenPair[]): AmenPick[] {
  const out: AmenPick[] = [];
  pairs.forEach((p) => {
    const lab = String(data[p.label] ?? "").trim();
    const ic = String(data[p.icon] ?? "").trim();
    if (lab || ic) out.push({ label: lab, url: ic });
  });
  return out;
}

/**
 * Rewrite the block's icon/label slots from a chosen set of labels, in order
 * (monolith `__amenApply`, L11087–11095). Returns the PATCH rather than mutating —
 * React owns block data here.
 *
 * Every declared slot is written, including the ones past the end of the pick
 * list: those are set to "" so the renderer's `amenTrimHtml` can drop the cell.
 * Leaving them alone is what ships a broken image.
 */
export function amenApply(pairs: AmenPair[], lib: AmenIcon[], labels: string[]): Record<string, string> {
  const byLab: Record<string, AmenIcon> = {};
  lib.forEach((a) => { byLab[a.label.toLowerCase()] = a; });
  const patch: Record<string, string> = {};
  pairs.forEach((p, i) => {
    const lab = labels[i];
    const a = lab ? byLab[String(lab).toLowerCase()] : null;
    patch[p.icon] = a ? a.url : "";
    patch[p.label] = a ? a.label : "";
  });
  return patch;
}

/**
 * Click adds to the end, click again removes and closes the gap. Full = the
 * earliest pick drops out, so the click always does something visible rather than
 * silently no-op'ing at the cap (monolith L13689–13703).
 *
 * Returns the new label list, or NULL when the click is refused because it would
 * go below AMEN_MIN — the caller shows the toast.
 */
export function amenToggle(cur: string[], label: string, cap: number): string[] | null {
  const next = cur.slice();
  const at = next.map((x) => String(x).toLowerCase()).indexOf(String(label).toLowerCase());
  if (at > -1) {
    /* min 2 (2026-08-18, per Jack) — one icon in a row built as a grid reads as a
       mistake. Unused cells above the floor are dropped at render, so going below
       the block's cell count is fine; going below two is not. */
    if (next.length <= AMEN_MIN) return null;
    next.splice(at, 1);
    return next;
  }
  next.push(label);
  if (next.length > cap) next.shift();
  return next;
}

/**
 * DROP UNUSED AMENITY CELLS AT RENDER (monolith `__amenTrimHtml`, L11050–11086).
 *
 * The blocks ship a FIXED grid — four `<td style="width:25%">` cells on
 * ECH_4Amenities, eight on the merged block — so an unused slot renders
 * `<img src="">`: a broken image in the send. Verified live before this existed,
 * and it is what Jack is looking at in the repo build today.
 *
 * Rather than ask a division to match the block's cell count, the empty cells are
 * removed and the survivors' widths re-evened, so 3 of 4 renders as three cells at
 * 33% instead of three at 25% with a hole on the right.
 *
 * DOM PARSING, NOT REGEX: these grids are nested tables and a regex that looks
 * right will eat the wrong `</td>` on some block eventually. `DOMParser` is
 * missing in a bare Node parity run, so the html comes back untouched there
 * rather than throwing — same failure posture as the monolith's try/catch.
 *
 * ⚠️ ONE DELIBERATE DEVIATION, AND THE MEASUREMENT BEHIND IT.
 * The monolith trims exactly ONE row — "the grid row is the one with the most
 * cells" (L11063–11067). That was written when every amenity block was a single
 * row of 3 or 4. Layne's LIVE merged block, "Amenities Section (Bar + 1-8 Icons)",
 * lays its eight icons out as TWO rows of four, and it ships with six defaults, so
 * the last two cells are empty. Running the monolith's own function against that
 * block's rendered markup in Chrome (2026-08-19): it picks the FIRST four-cell row
 * — strict `>` never replaces a tie — finds 0 empty cells there, and returns the
 * html UNCHANGED. Both broken images survive. That is precisely what Jack is
 * looking at.
 * So the row SELECTION is generalised: every row that has cells and at least one
 * empty one gets the monolith's algorithm, unchanged. A row left with nothing is
 * removed outright, or three picks on an eight-cell block would still ship a whole
 * row of broken images.
 */
export function amenTrimHtml(html: string): string {
  try {
    if (!/<img[^>]*src=""/.test(html)) return html;
    if (typeof DOMParser === "undefined") return html;
    const doc = new DOMParser().parseFromString(html, "text/html");
    /* a grid row: two or more cells, at least one of them now empty */
    const rows: Element[] = Array.prototype.slice
      .call(doc.querySelectorAll("tr"))
      .filter((r: Element) => r.children.length >= 2 && r.querySelector('img[src=""]'));
    if (!rows.length) return html;
    let touched = false;

    /** the px width a cell was authored with, or 0 when it isn't sized in px */
    const pxOf = (td: Element): number => {
      const attr = String(td.getAttribute("width") || "");
      if (/^\s*\d+(\.\d+)?\s*(px)?\s*$/.test(attr)) return parseFloat(attr);
      const m = String(td.getAttribute("style") || "").match(/width\s*:\s*([\d.]+)px/i);
      return m ? parseFloat(m[1]) : 0;
    };

    /* PASS 1 — remove the empty cells (and any row they leave empty).
       The row's TOTAL authored px width is captured BEFORE removal: it is the only
       record of how wide the row was meant to be, and pass 2 needs it to refill a
       px-sized row without measuring layout (this runs in jsdom too). */
    const survivors: { row: Element; keep: Element[]; pxTotal: number }[] = [];
    rows.forEach((row) => {
      const cells: Element[] = Array.prototype.slice.call(row.children);
      const pxTotal = cells.reduce((n, td) => n + pxOf(td), 0);
      const allPx = cells.every((td) => pxOf(td) > 0);
      const keep: Element[] = [];
      cells.forEach((td) => {
        const im = td.querySelector('img[src=""]');
        if (im) td.parentNode?.removeChild(td);
        else keep.push(td);
      });
      if (keep.length === cells.length) return;             /* nothing was empty here */
      touched = true;
      /* the whole row went — drop it rather than leave an empty <tr> holding padding */
      if (!keep.length) { row.parentNode?.removeChild(row); return; }
      survivors.push({ row, keep, pxTotal: allPx ? pxTotal : 0 });
    });
    if (!touched) return html;

    /* PASS 2 — RE-EVEN ONLY A SINGLE-ROW GRID (2026-08-20).
       Jack, on the merged 8-icon block with six picks: "why is the 2nd row not
       aligning with the top?" Measured in Chrome: row 1 was four cells at
       width="140" (560px in total) and row 2 was two cells THIS FUNCTION had
       rewritten to width="50%" — which, inside that block's shrink-to-fit table,
       resolved to 71px each. A 142px row sat under a 560px one.

       Two separate faults, both here:
        1. An authored PIXEL width was replaced with a PERCENTAGE. Layne's merged
           block sizes its cells in px, and a % only means what you intend inside a
           table whose own width resolves — this one's does not.
        2. Even in percent, re-evening a PARTIAL row misaligns it by construction:
           two cells at 50% sit under four at 25%. A wrapped grid's last row lines
           up only if its cells keep the width the block already gave them.

       So the count is taken AFTER the removal pass, not before. Three picks on an
       eight-cell block leaves ONE row standing, and that row should still fill the
       width — which is the ECH_4Amenities behaviour Jack approved. Two rows still
       standing means it is a wrapped grid, and every survivor keeps its authored
       width so the columns line up.

       REFILL IN THE ROW'S OWN UNIT (added the same day, after the first cut of this
       fix bailed out of the px case entirely). "Never rewrite px as %" is right, but
       skipping px rows altogether traded the misaligned second row for a hole on the
       right: on the merged block every cell is width="140", so 2 picks left 280px of
       icons in a 560px row and only 4 or 8 ever landed clean. Since the picker
       preselects however many amenities a community actually claims (2–8), the ragged
       counts are the normal case, not the edge. A px row therefore refills in PIXELS
       — the row's captured total divided by the survivors — and a % row in percent.
       Same intent, expressed in whatever unit the block chose. */
    const standing: number = Array.prototype.slice
      .call(doc.querySelectorAll("tr"))
      .filter((r: Element) => r.children.length >= 2 && r.querySelector("img")).length;

    if (standing <= 1) {
      survivors.forEach(({ keep, pxTotal }) => {
        if (pxTotal > 0) {
          /* px row: hand each survivor an equal share of the width the row had */
          const px = Math.round(pxTotal / keep.length);
          keep.forEach((td) => {
            const st = String(td.getAttribute("style") || "");
            td.setAttribute("style", st.replace(/width\s*:\s*[\d.]+px\s*;?/i, "") + "width: " + px + "px;");
            if (td.hasAttribute("width")) td.setAttribute("width", String(px));
          });
          return;
        }
        const pct = 100 / keep.length;
        keep.forEach((td) => {
          const st = String(td.getAttribute("style") || "");
          td.setAttribute("style", st.replace(/width\s*:\s*[\d.]+%\s*;?/i, "") + "width: " + pct.toFixed(4) + "%;");
          if (td.hasAttribute("width")) td.setAttribute("width", pct.toFixed(2) + "%");
        });
      });
    }
    return doc.body.innerHTML;
  } catch {
    /* a malformed grid must never take the canvas down */
    return html;
  }
}

/**
 * ── HER ROW SPLIT IS THE LAYOUT, NOT OUR WRAP-AFTER-4 (2026-08-27) ─────────────────
 *
 * Layne, on the merged "Amenities Section (Bar + 1-8 Icons)" block: the icons do not
 * simply wrap. She declares the split per count —
 *
 *   "rowSplit": { "5": [3,2], "6": [3,3], "7": [4,3], "8": [4,4] }
 *
 * — so five icons are 3 then 2, NOT 4 then 1. Cells have to MOVE between rows, which
 * `amenTrimHtml` cannot do: it only deletes empty cells in place, so five picks left
 * four on row 1 and a lone fifth under them. Her own note says why that reads wrong:
 * every cell is locked at 140px and "a row that is not full is centered as a group",
 * so 4+1 hangs one icon under a full row instead of balancing two short ones.
 *
 * WHAT THIS DOES, and only this:
 *   · reads the surviving icon cells out of the block's `table.am-row` grids, in order;
 *   · re-deals them across the rows per the DECLARED split — `rowSplit` when the block
 *     carries one, otherwise the older `layout.maxPerRow` wrap, which is exactly
 *     today's behaviour, so a block that declares neither is untouched;
 *   · drops a row left with no cells, and the row-gap / divider table with it;
 *   · honours `rowDivider.showWhenIconCountAtLeast` — the 1px line currently renders
 *     even on a single row, because the trim strips row 2's icons and leaves the
 *     structure around them standing;
 *   · CHANGES NO WIDTH. The 140px cell is locked and the row table is already
 *     `align="center"`, so a short row centres itself. This is the one place the older
 *     px-refill logic in `amenTrimHtml` would have been wrong.
 *
 * Structure it relies on, read off the live block (2026-08-20 pull, kept as
 * `__fixtures__/ech-amenities.fixture.json`): `<table class="am-row">` → one `<tr>` →
 * `<td class="am-cell" width="140">` per icon, two row tables with a gap table between,
 * each cell marked by an `ICON n` comment. Anything that is not that shape falls
 * through to `amenTrimHtml` untouched.
 */
export interface AmenRowLayout {
  maxPerRow?: number;
  maxRows?: number;
  /** icon count → cells per row, e.g. { "5": [3,2] } */
  rowSplit?: Record<string, number[]> | null;
  /** the 1px line between rows, and the count from which it earns its place */
  rowDivider?: { showWhenIconCountAtLeast?: number } | null;
}
/**
 * ⚠️ WHERE THESE KEYS ACTUALLY LIVE (measured on the live block, 2026-08-27).
 *
 * `ECH_Amenities` id **1029215** carries `rowSplit` and `rowDivider` NESTED INSIDE
 * `layout`, not at the top level of the guardrails:
 *
 *   layout: { minIcons:1, maxIcons:8, maxPerRow:4, maxRows:2, cellWidth:140,
 *             rowGap:25, partialRow:"centered", mobileBreakpoint:480,
 *             rowSplit:{ "1":[1],…,"5":[3,2],"6":[3,3],"7":[4,3],"8":[4,4] },
 *             rowDivider:{ height:1, color:"#F2F2F2", insetEachSide:20,
 *                          renderedWidth:560, spaceAbove:25, spaceBelow:25,
 *                          showWhenIconCountAtLeast:5, note:… } }
 *
 * The first cut of this read them off the top level, which would have found neither —
 * the exact class of miss that started this whole thread. Both shapes are accepted so
 * a later move of the keys cannot break it again, with `layout` winning.
 */
export interface AmenRowRules extends AmenRowLayout {
  layout?: AmenRowLayout | null;
}

/** the effective layout: `layout` first, top level as a fallback */
function lay(rules?: AmenRowRules | null): AmenRowLayout {
  const l = (rules && rules.layout) || {};
  return {
    maxPerRow: l.maxPerRow != null ? l.maxPerRow : (rules ? rules.maxPerRow : undefined),
    maxRows: l.maxRows != null ? l.maxRows : (rules ? rules.maxRows : undefined),
    rowSplit: l.rowSplit || (rules && rules.rowSplit) || null,
    rowDivider: l.rowDivider || (rules && rules.rowDivider) || null,
  };
}

/** the cells-per-row plan for `n` icons, from the block's own declaration */
export function amenSplitFor(n: number, rules?: AmenRowRules | null): number[] {
  if (n <= 0) return [];
  const L = lay(rules);
  const declared = L.rowSplit && L.rowSplit[String(n)];
  if (Array.isArray(declared) && declared.length) {
    const plan = declared.map((x) => Math.max(0, Math.floor(Number(x) || 0))).filter((x) => x > 0);
    const total = plan.reduce((a, b) => a + b, 0);
    /* a declaration that does not add up is a typo in the header, not a layout — fall
       through rather than silently dropping or duplicating somebody's icon */
    if (total === n) return plan;
  }
  const per = Math.max(1, Math.floor(L.maxPerRow || 4));
  const out: number[] = [];
  let left = n;
  while (left > 0) { out.push(Math.min(per, left)); left -= per; }
  return out;
}

/** true when the divider has earned its place at this icon count */
export function amenDividerVisible(n: number, rules?: AmenRowRules | null): boolean {
  const min = lay(rules).rowDivider && lay(rules).rowDivider!.showWhenIconCountAtLeast;
  if (typeof min !== "number" || !isFinite(min)) return amenSplitFor(n, rules).length > 1;
  return n >= min && amenSplitFor(n, rules).length > 1;
}

export function amenRowsHtml(html: string, rules?: AmenRowRules | null): string {
  try {
    if (typeof DOMParser === "undefined") return html;
    if (!/class="[^"]*\bam-row\b/.test(html)) return html;
    const doc = new DOMParser().parseFromString(html, "text/html");
    const rowTables: Element[] = Array.prototype.slice.call(doc.querySelectorAll("table.am-row"));
    if (!rowTables.length) return html;

    /* every icon cell in document order, and the ones that survived the fill */
    const cells: Element[] = [];
    rowTables.forEach((t) => Array.prototype.slice.call(t.querySelectorAll("td.am-cell")).forEach((c: Element) => cells.push(c)));
    if (!cells.length) return html;
    const kept = cells.filter((c) => {
      const im = c.querySelector("img");
      return !!im && String(im.getAttribute("src") || "").trim() !== "";
    });
    cells.filter((c) => kept.indexOf(c) < 0).forEach((c) => c.parentNode && c.parentNode.removeChild(c));

    const plan = amenSplitFor(kept.length, rules);
    /* re-deal the survivors across the row tables the block already has */
    let at = 0, droppedGroup = false;
    rowTables.forEach((t, i) => {
      const want = plan[i] || 0;
      const tr = t.querySelector("tr");
      if (!tr) return;
      const mine = kept.slice(at, at + want);
      at += want;
      /* append in order — a cell already in this row is moved, not duplicated */
      mine.forEach((c) => tr.appendChild(c));
      if (!want) {
        /* ── REMOVE HER GROUP, NOT A SIBLING (2026-08-27, off the live block) ──────
           The first cut deleted the row table and, if the previous sibling was a
           cell-less table, that too. On the live block that is wrong: row 2 is not a
           sibling of a gap table, it is NESTED inside a wrapper her markup calls the
           SECOND ROW GROUP, which holds 25px space → the 1px #F2F2F2 divider → 25px
           space → the row 2 icons. Her own comment: "DELETE THIS ONE TABLE to go down
           to a single row of 4 or fewer… Grouping them means the divider cannot be
           left behind by accident."
           So walk up to the outermost table that contains THIS row and no other
           `am-row`, and remove that. Falls back to removing the row table itself when
           there is no such wrapper, which is the older block's shape. */
        let drop: Element = t;
        let up: Element | null = t.parentElement;
        while (up) {
          if (up.tagName.toLowerCase() === "table") {
            const rowsIn = up.querySelectorAll("table.am-row").length;
            if (rowsIn !== 1) break;   /* this wrapper holds another row too — stop below it */
            drop = up;
          }
          up = up.parentElement;
        }
        drop.parentNode && drop.parentNode.removeChild(drop);
        /* her AUTHORING comments bracket that table — "SECOND ROW GROUP … DELETE THIS
           ONE TABLE" and "END SECOND ROW GROUP". Once the deletion is done they are
           stale instructions sitting in a shipped email, and Creative reads this html.
           They go with the group. */
        droppedGroup = true;
      }
    });
    if (droppedGroup) {
      const walk = doc.createTreeWalker(doc.body, 128 /* NodeFilter.SHOW_COMMENT */);
      const stale: Node[] = [];
      let c: Node | null = walk.nextNode();
      while (c) { if (/SECOND ROW GROUP/i.test(String(c.nodeValue || ""))) stale.push(c); c = walk.nextNode(); }
      stale.forEach((x) => x.parentNode && x.parentNode.removeChild(x));
    }

    /* NO INDEPENDENT DIVIDER PASS. Her note is explicit: the divider lives inside the
       SECOND ROW GROUP with the row it separates, "so removing that table removes the
       divider automatically. Nothing needs to hide or show it independently." Since a
       second row exists only at 5+ icons — which is also her
       `rowDivider.showWhenIconCountAtLeast` — the group removal above is the whole
       behaviour. `amenDividerVisible` is kept as the readable statement of that rule
       and is asserted in the tests, but nothing here needs to act on it separately. */
    return doc.body.innerHTML;
  } catch {
    /* a malformed grid must never take the canvas down */
    return html;
  }
}
